import { supabase, Lumisial } from '../lib/supabase';
import { RELATIONS } from '../lib/foreignKeys';

/** Lo mínimo que el selector de destino de traslado necesita mostrar. */
export interface LumisialDestinoTraslado {
  readonly id: string;
  readonly nombre: string;
  readonly ciudad: string;
}

export const lumisialService = {
  async getAll(): Promise<Lumisial[]> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select(RELATIONS.LUMISIAL_FULL)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async listarTodos(): Promise<Lumisial[]> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select(RELATIONS.LUMISIAL_FULL)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async listarConDetalles(): Promise<Lumisial[]> {
    const lumisiales = await this.listarTodos();

    const lumisialesConDetalles = await Promise.all(
      lumisiales.map(async (lumisial) => {
        const { count, error } = await supabase
          .from('miembros')
          .select('*', { count: 'exact', head: true })
          .eq('lumisial_actual_id', lumisial.id)
          .eq('estado_miembro', 'activo');

        if (error) {
          console.error(`Error contando miembros para lumisial ${lumisial.id}:`, error);
          return { ...lumisial, cantidad_miembros: 0 };
        }

        return {
          ...lumisial,
          cantidad_miembros: count || 0
        };
      })
    );

    return lumisialesConDetalles;
  },

  async getById(id: string): Promise<Lumisial | null> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select(RELATIONS.LUMISIAL_FULL)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async crear(lumisial: Omit<Lumisial, 'id' | 'created_at' | 'provincia_eclesiastica' | 'diocesis'>): Promise<Lumisial> {
    const { data, error } = await supabase
      .from('lumisiales')
      .insert(lumisial)
      .select(RELATIONS.LUMISIAL_FULL)
      .single();

    if (error) throw error;
    return data;
  },

  async actualizar(id: string, lumisial: Partial<Omit<Lumisial, 'id' | 'created_at' | 'provincia_eclesiastica' | 'diocesis' | 'cantidad_miembros'>>): Promise<Lumisial> {
    const { data, error } = await supabase
      .from('lumisiales')
      .update(lumisial)
      .eq('id', id)
      .select(RELATIONS.LUMISIAL_FULL)
      .single();

    if (error) throw error;
    return data;
  },

  async desactivar(id: string): Promise<Lumisial> {
    return this.actualizar(id, { activo: false });
  },

  async activar(id: string): Promise<Lumisial> {
    return this.actualizar(id, { activo: true });
  },

  async obtenerCiudadesPorProvincia(provinciaEclesiasticaId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select('ciudad')
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('activo', true)
      .not('ciudad', 'is', null);

    if (error) throw error;

    const ciudadesUnicas = [...new Set(data?.map(l => l.ciudad).filter(Boolean) || [])];
    return ciudadesUnicas.sort((a, b) => a.localeCompare(b, 'es'));
  },

  async obtenerPorProvinciaYCiudad(
    provinciaEclesiasticaId: string,
    ciudad: string
  ): Promise<Lumisial[]> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select(RELATIONS.LUMISIAL_FULL)
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('ciudad', ciudad)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async obtenerPorDiocesis(diocesisId: string): Promise<Lumisial[]> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select(RELATIONS.LUMISIAL_FULL)
      .eq('diocesis_id', diocesisId)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getByDiocesis(diocesisId: string): Promise<Lumisial[]> {
    return this.obtenerPorDiocesis(diocesisId);
  },

  /**
   * Destinos posibles de un traslado. No usa `obtenerPorDiocesis`: la política de lectura de
   * `lumisiales` limita a un rol local a su propio Lumisial, así que el selector nunca mostraba
   * los demás (bloqueo B2 del UAT). La función de base de datos autoriza con el mismo criterio
   * que `trasladar_miembro` —autoridad sobre el miembro de origen— y devuelve solo lo que el
   * selector necesita.
   */
  async obtenerDestinosTraslado(
    miembroOrigenId: string,
    diocesisDestinoId: string
  ): Promise<LumisialDestinoTraslado[]> {
    const { data, error } = await supabase.rpc('listar_lumisiales_destino_traslado', {
      p_miembro_origen_id: miembroOrigenId,
      p_diocesis_destino_id: diocesisDestinoId,
    });

    if (error) throw error;
    return data || [];
  },

  async getByProvincia(provinciaId: string): Promise<Lumisial[]> {
    const { data, error } = await supabase
      .from('lumisiales')
      .select(RELATIONS.LUMISIAL_FULL)
      .eq('provincia_eclesiastica_id', provinciaId)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  }
};
